import {
  Box,
  Typography,
  CircularProgress,
  Divider,
  Grid,
  Checkbox,
  FormControlLabel,
  Switch,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { useEffect, useState, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AttributeSelectors } from '../components/inputs/AttributeSelectors';
import { ProductFinderResults } from '../components/ProductfinderResults';
import Split from 'react-split';
import IIndexValue from '../models/iIndexValue';
import { toast } from 'react-toastify';
import { useOktaAuth } from '@okta/okta-react';
import { customerRequest, getGenerativeSummary } from '../providers/productFinderAPI';
import TextAndFilePasteInput from '../components/inputs/textAndFileInput';
import { IProductSummaryResponse } from '../models/iProductSearchRequest';
import ProductTypes from '../enum/ProductTypesEnum';
import { productTypeConfigs } from '../types/ProductTypeFilter';
import { MultiSelectAutocomplete } from '../components/MultiSelectAutocomplete';
import { SearchProvider, useSearch } from '../context/SearchContext';
import { QueryClientProvider } from '@tanstack/react-query';
import { queryClient } from '../providers/queryClient';
import { useIndexes } from '../hooks/useIndexes';
import { useSalesOrgs } from '../hooks/useSalesOrgs';
import { QueryParams } from '../enum/QueryParams';
import ISalesOrg from '../models/iSalesOrg';

export const ProductFinderContent = () => {
  const { authState } = useOktaAuth();
  const { state, dispatch } = useSearch();
  const { enabledProductTypes, selectedIndexes, selectedSalesOrgs } = state;
  const [searchParams] = useSearchParams();
  const [showGeminiSearch, setShowGeminiSearch] = useState<boolean>(false);

  const accessToken = authState?.accessToken?.accessToken;

  const { data: indexValues = [], isLoading: indexesLoading, error: indexesError } = useIndexes(accessToken || '');
  const { data: salesOrgs = [], isLoading: salesOrgsLoading, error: salesOrgsError } = useSalesOrgs(accessToken || '');

  const isLoading = indexesLoading || salesOrgsLoading;

  const [correlationId, setCorrelationId] = useState<string | null>(null);
  const hasInitializedFromUrl = useRef<boolean>(false);

  const theme = useTheme();
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));

  const isProductTypeEnabled = (type: ProductTypes): boolean => {
    return enabledProductTypes.includes(type);
  };

  // Update available options when data is loaded
  useEffect(() => {
    if (indexValues.length > 0) {
      const allIndexNames = indexValues.map((index: IIndexValue) => index.indexName);
      dispatch({ type: 'SET_AVAILABLE_INDEXES', payload: allIndexNames });
      if (selectedIndexes.length === 0) {
        dispatch({ type: 'SET_SELECTED_INDEXES', payload: [] }); // Empty = search all
      }
    }
  }, [indexValues, dispatch, selectedIndexes.length]);

  useEffect(() => {
    if (salesOrgs.length > 0) {
      dispatch({ type: 'SET_AVAILABLE_SALES_ORGS', payload: salesOrgs });
    }
  }, [salesOrgs, dispatch]);

  // Initialize selected sales orgs and indexes from URL parameter (once only)
  useEffect(() => {
    // Wait for all data to load and ensure we haven't initialized yet
    if (!isLoading && !hasInitializedFromUrl.current) {
      const salesAreaParam = searchParams.get(QueryParams.SalesArea);
      const productGroupParam = searchParams.get(QueryParams.ProductGroup);

      // Handle Sales Org
      if (salesOrgs.length > 0 && selectedSalesOrgs.length === 0 && salesAreaParam) {
        const salesOrg = salesOrgs.find((org: ISalesOrg) => org.salesOrgId === Number.parseInt(salesAreaParam, 10));
        if (salesOrg) {
          dispatch({ type: 'SET_SELECTED_SALES_ORGS', payload: [salesOrg] });
        }
      }

      // Handle Product Group
      if (indexValues.length > 0 && selectedIndexes.length === 0 && productGroupParam) {
        const index = indexValues.find((idx: IIndexValue) => idx.indexName === productGroupParam);
        if (index) {
          dispatch({ type: 'SET_SELECTED_INDEXES', payload: [index.indexName] });
        }
      }

      // Mark as initialized so this doesn't run again when the user makes manual changes
      hasInitializedFromUrl.current = true;
    }
  }, [isLoading, salesOrgs, indexValues, searchParams, selectedSalesOrgs.length, selectedIndexes.length, dispatch]);

  // Handle errors
  useEffect(() => {
    if (indexesError) {
      toast.error('Failed to load indexes. Please try again later.');
    }
    if (salesOrgsError) {
      toast.error('Failed to load sales organizations. Please try again later.');
    }
  }, [indexesError, salesOrgsError]);

  const onIndexChange = (indexes: string[]) => {
    dispatch({ type: 'SET_SELECTED_INDEXES', payload: indexes });
  };

  const toggleProductType = (type: ProductTypes) => {
    dispatch({ type: 'TOGGLE_PRODUCT_TYPE', payload: type });
  };

  const handleCustomerRequest = async (text: string, files: File[]) => {
    const accessToken = authState?.accessToken?.accessToken;
    const salesOrgIds = selectedSalesOrgs.map((org: ISalesOrg) => org.salesOrgId);

    // Customer request only works with single index
    if (accessToken && selectedIndexes.length === 1) {
      const { filters, correlationId: newCorrelationId } = await customerRequest(
        accessToken,
        text,
        selectedIndexes[0],
        salesOrgIds,
        files
      );
      setCorrelationId(newCorrelationId);
      if (filters.length > 0) {
        const updatedFilters = filters.map((filter, index) => ({
          ...filter,
          id: index + 1,
        }));
        dispatch({ type: 'SET_FILTERS', payload: { filters: updatedFilters } });
        toast.success('Customer request processed successfully.');
      } else {
        toast.info('No matching products found for the provided request.');
      }
    }
  };

  const handleGeminiSearch = async (text: string, files: File[]): Promise<IProductSummaryResponse> => {
    const accessToken = authState?.accessToken?.accessToken;

    if (!accessToken) {
      toast.error('Authentication required. Please log in to continue.');
      return Promise.reject('Authentication required');
    }

    if (selectedIndexes.length !== 1) {
      toast.error('Please select exactly one product group for Gemini search.');
      return Promise.reject('Invalid selection');
    }

    return await getGenerativeSummary(accessToken, text, selectedIndexes[0], files);
  };

  const handleSearchComplete = () => {
    setCorrelationId(null);
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <Split
        sizes={[90, 10]}
        minSize={350}
        snapOffset={30}
        dragInterval={1}
        direction="horizontal"
        style={{ display: 'flex', height: '100%' }}
        gutterSize={10}
        gutterAlign="center"
      >
        {isLoading ? (
          <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ flexGrow: 1 }} p={3}>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Welcome to product finder. To get started quickly, select a business and product to begin your search.
            </Typography>
            <Grid container direction="row" sx={{ width: '100%', alignItems: 'stretch' }}>
              <Grid
                sx={{
                  flexDirection: 'column',
                  height: '100%',
                  display: 'flex',
                  gap: 2,
                  pt: 3,
                  pb: 3,
                  pr: 3,
                  pl: 0,
                }}
                size={{
                  xs: 12,
                  md: 2
                }}>
                <Typography variant="h6" sx={{ textAlign: 'left', alignSelf: 'flex-start' }}>
                  Select Organizations
                </Typography>
                <MultiSelectAutocomplete
                  options={salesOrgs}
                  value={selectedSalesOrgs}
                  onChange={(_, value) => dispatch({ type: 'SET_SELECTED_SALES_ORGS', payload: value })}
                  getOptionLabel={(option) => option.name}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  label="Sales Org"
                  totalOptionsCount={salesOrgs.length}
                />
                <MultiSelectAutocomplete
                  options={indexValues}
                  value={
                    selectedIndexes
                      .map((indexName) => indexValues.find((option: IIndexValue) => option.indexName === indexName))
                      .filter(Boolean) as IIndexValue[]
                  }
                  onChange={(_, value) => onIndexChange(value.map((v) => v.indexName))}
                  getOptionLabel={(option) => option.displayName}
                  isOptionEqualToValue={(option, value) => option.indexName === value.indexName}
                  label="Product Group"
                  disabled={false}
                  totalOptionsCount={indexValues.length}
                />
                {productTypeConfigs.map((config) => (
                  <FormControlLabel
                    key={config.type}
                    control={
                      <Checkbox
                        checked={isProductTypeEnabled(config.type)}
                        onChange={() => toggleProductType(config.type)}
                      />
                    }
                    label={config.label}
                  />
                ))}
                <FormControlLabel
                  control={
                    <Switch
                      checked={showGeminiSearch}
                      onChange={() => setShowGeminiSearch((prev) => !prev)}
                      size="small"
                    />
                  }
                  label="Gemini Chat"
                />
              </Grid>
              <Grid
                direction={'row'}
                sx={{
                  display: 'flex',
                  alignItems: 'stretch',
                  flexGrow: showGeminiSearch ? 0 : 1,
                }}
                size={{
                  xs: 12,
                  sm: showGeminiSearch ? 6 : 10,
                  md: showGeminiSearch ? 5 : 10
                }}>
                {isMdUp && <Divider orientation={'vertical'} flexItem />}
                <AttributeSelectors />
              </Grid>

              {showGeminiSearch && (
                <Grid
                  direction={isSmUp ? 'row' : 'column'}
                  sx={{
                    display: 'flex',
                    alignItems: 'stretch',
                  }}
                  size={{
                    xs: 12,
                    sm: 6,
                    md: 5
                  }}>
                  <>
                    <Divider orientation={isSmUp ? 'vertical' : 'horizontal'} flexItem>
                      OR
                    </Divider>
                    <Box
                      sx={{
                        flex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'flex-start',
                        alignItems: 'stretch',
                      }}
                    >
                      <TextAndFilePasteInput
                        onSubmit={handleCustomerRequest}
                        onGeminiSubmit={handleGeminiSearch}
                        disabled={selectedIndexes.length !== 1}
                      />
                    </Box>
                  </>
                </Grid>
              )}
            </Grid>
            <Box sx={{ width: '100%', overflow: 'auto' }}>
              <ProductFinderResults correlationId={correlationId} onSearchComplete={handleSearchComplete} />
            </Box>
          </Box>
        )}
      </Split>
    </Box>
  );
};

export const ProductFinderPage = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <SearchProvider>
        <ProductFinderContent />
      </SearchProvider>
    </QueryClientProvider>
  );
};
